import React, { useCallback, useMemo, useRef, useState } from 'react';
import { GradientText, InputLabel, Poppins, Spacer } from 'src/common';
import Button from 'src/components/form/Button';
import styled from 'styled-components';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ConfirmDeleteModal } from 'src/components/modals';
import { TabWrap } from 'src/components/TabsMenu';
import { useAuth } from 'src/state/auth';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import AutoGrowingInput from 'src/components/form/AutoGrowingInput';
import {
  UpsertOrganisationMutationVariables,
  useArchiveOrganisationMutation,
  useOrganisationQuery,
  useProfileSettingsOptionsQuery,
  useUpsertOrganisationMutation,
} from 'src/graphql/_generated';
import { useCache } from 'src/graphql/useCache';
import { OrganisationTypeSelect, BusinessTypeSelect, OrganisationSelect } from 'src/components/select';
import { getDiff } from 'src/utils/getDiff';
import { Branding, BrandingHandle } from './comps';

const Div = styled(TabWrap)`
  background-color: ${({ theme }) => theme.colors.white};
  position: relative;

  form {
    .contents {
      display: grid;
      grid-column-gap: 50px;
      grid-row-gap: 20px;
      grid-template-columns: 1fr 1fr;
    }

    .controls {
      display: flex;
      align-items: center;
      justify-content: space-between;
      grid-gap: 13px;
      gap: 13px;
    }
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    form {
      .contents {
        grid-template-columns: 1fr;
        grid-gap: 20px;
      }
    }
  }
`;

interface DetailsProps {
  organisationId: string;
}

export const Details: React.FC<DetailsProps> = ({ organisationId }) => {
  const { data: organisationData } = useOrganisationQuery({ variables: { id: organisationId } });
  const organisation = organisationData?.organisation;
  const [archiveWarningModalOpen, setArchiveWarningModalOpen] = useState(false);
  const [hasLogoStaged, setHasLogoStaged] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const brandingRef = useRef<BrandingHandle>(null);

  const { flatOrganisations, features } = useAuth();
  const { data: settingsData } = useProfileSettingsOptionsQuery();

  const { refetchQuery } = useCache();
  const canEditSettings = organisation?.actionsOnThisOrganisation?.canEditSettings;
  const brandingEnabled = features['app.partner.branding'];

  const validationSchema = useMemo(() => {
    const originalName = organisation?.name?.trim().toLowerCase() || '';
    const existingOrgNames =
      flatOrganisations
        ?.filter((o) => o.recordType === 'O')
        .map((o) => o.name.trim().toLowerCase())
        .filter((name) => name !== originalName) || [];

    return Yup.object({
      name: Yup.string()
        .required('Required')
        .test('unique-name', 'Organisation name already exists', (value) => {
          if (!value) return true;
          return !existingOrgNames.includes(value.trim().toLowerCase());
        }),
      primaryContact: Yup.string().email('Invalid email address').required('Required'),
      billingContact: Yup.string().email('Invalid email address').nullable(),
    });
  }, [flatOrganisations, organisation?.name]);
  const refetch = () => refetchQuery({ include: ['organisation', 'flatOrganisationsHome', 'flatOrganisationsNav'] });

  const [update, { error }] = useUpsertOrganisationMutation();

  const [archiveOrganisation, { loading: isArchiving }] = useArchiveOrganisationMutation({
    onCompleted: () => refetch(),
    update: (cache) => {
      cache.modify({
        id: cache.identify({ __typename: 'Organisation', id: organisationId }),
        fields: {
          isArchived: () => true,
        },
      });
    },
  });

  const handleLogoStateChange = useCallback((hasFile: boolean, isUploading: boolean) => {
    setHasLogoStaged(hasFile);
    setIsUploadingLogo(isUploading);
  }, []);

  const initialValues = {
    name: organisation?.name,
    primaryContact: organisation?.primaryContact,
    billingContact: organisation?.billingContact,
    domain: organisation?.domain,
    organisationType: organisation?.organisationType,
    businessType: organisation?.businessType,
    parentId: organisation?.parentId,
  } satisfies UpsertOrganisationMutationVariables as UpsertOrganisationMutationVariables;

  const {
    values,
    handleChange,
    handleBlur,
    touched,
    errors,
    handleSubmit,
    isValid,
    isSubmitting,
    dirty,
    setFieldValue,
    setFieldTouched,
  } = useFormik({
    validationSchema,
    initialValues,
    onSubmit: async (values) => {
      const diff = getDiff(initialValues, values);
      const payload = { organisationId: organisation!.id, ...diff } as UpsertOrganisationMutationVariables;

      if (brandingRef.current?.hasLogoStaged) {
        const logoKey = await brandingRef.current.uploadStagedLogo();
        if (logoKey === null) {
          return;
        }
        payload.logoKey = logoKey;
      }

      await update({ variables: payload });
      await refetch();
      if (brandingRef.current) {
        brandingRef.current.clearStaged();
      }
      if (payload.logoKey) {
        brandingRef.current?.showSuccess('Logo updated successfully!', 'Enter your workspace to see the changes made.');
      }
    },
    enableReinitialize: true,
  });

  const selectedSettingsOption = settingsData?.profile?.orgSettingsOptions?.find(
    (o) => o.organisationType === values.organisationType,
  );
  const canSubmit =
    (dirty ? isValid : true) && (dirty || (brandingEnabled && hasLogoStaged)) && !isSubmitting && !isUploadingLogo;

  const availableOrganisationTypes = settingsData?.profile?.orgSettingsOptions
    ?.map((el) => el.organisationType)
    .filter((el) => el !== null && el !== undefined);

  if (!organisation) return null;

  return (
    <>
      {archiveWarningModalOpen && (
        <>
          <ConfirmDeleteModal
            title={null}
            onConfirm={() => {
              archiveOrganisation({ variables: { id: organisation.id } });
              setArchiveWarningModalOpen(false);
            }}
            onClose={() => setArchiveWarningModalOpen(false)}
            confirmText="ARCHIVE"
          >
            <Poppins px={18} weight={600} color="cflowerBlue">
              Are you sure you want to archive{' '}
              <Poppins px={18} weight={600} color="prussianBlue">
                {organisation.name}
              </Poppins>{' '}
              ?
            </Poppins>
          </ConfirmDeleteModal>
        </>
      )}
      <Div>
        <GradientText>DETAILS</GradientText>
        <Spacer $px={32} />
        <Branding ref={brandingRef} organisation={organisation} onLogoStateChange={handleLogoStateChange} />
        <Spacer $px={32} />
        <form onSubmit={handleSubmit}>
          <div className="contents">
            <div>
              <InputLabel>NAME *</InputLabel>
              <AutoGrowingInput
                id="name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.name}
                fullWidth
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />
            </div>

            <div>
              <InputLabel>ORGANISATION EMAIL DOMAIN</InputLabel>
              <AutoGrowingInput value={organisation.domain?.replace(/^@/, '')} fullWidth disabled />
            </div>

            <div>
              <InputLabel>PRIMARY CONTACT EMAIL ADDRESS *</InputLabel>
              <AutoGrowingInput
                id="primaryContact"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.primaryContact}
                type="email"
                fullWidth
                error={touched.primaryContact && Boolean(errors.primaryContact)}
                helperText={touched.primaryContact && errors.primaryContact}
              />
            </div>

            <div>
              <InputLabel>BILLING CONTACT EMAIL ADDRESS</InputLabel>
              <AutoGrowingInput
                id="billingContact"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.billingContact}
                type="email"
                fullWidth
                error={touched.billingContact && Boolean(errors.billingContact)}
                helperText={touched.billingContact && errors.billingContact}
              />
            </div>

            <div>
              <InputLabel tooltip="Changing the Organisation Type will automatically update and reset the related configuration settings below.">
                ORGANISATION TYPE
              </InputLabel>
              <OrganisationTypeSelect
                onChange={(o) => {
                  setFieldValue('organisationType', o.value);
                  setFieldValue('businessType', null);
                }}
                onBlur={() => setFieldTouched('organisationType', true)}
                value={values.organisationType}
                restrictedTo={availableOrganisationTypes}
                css="width: 100%;"
                error={touched.organisationType && Boolean(errors.organisationType)}
                disabled={!organisation.actionsOnThisOrganisation?.canEditOrganisationType}
              />
            </div>

            {selectedSettingsOption?.businessType ? (
              <div>
                <InputLabel>BUSINESS TYPE</InputLabel>
                <BusinessTypeSelect
                  restrictedTo={selectedSettingsOption?.businessType}
                  onChange={(o) => setFieldValue('businessType', o.value)}
                  onBlur={() => setFieldTouched('businessType', true)}
                  value={values.businessType}
                  css="width: 100%;"
                  error={touched.businessType && Boolean(errors.businessType)}
                />
              </div>
            ) : (
              <div />
            )}

            {organisation.actionsOnThisOrganisation?.canSeeParent && (
              <div>
                <InputLabel>PARENT ORGANISATION</InputLabel>
                <OrganisationSelect
                  value={values.parentId}
                  onChange={(o) => setFieldValue('parentId', o.value)}
                  onBlur={() => setFieldTouched('parentId', true)}
                  css="width: 100%;"
                  error={touched.parentId && Boolean(errors.parentId)}
                  disabled={!organisation.actionsOnThisOrganisation?.canEditParent}
                />
              </div>
            )}
          </div>

          {error && (
            <>
              <Spacer $px={20} />
              <div className="err">{error.message || 'Something went wrong'}</div>
            </>
          )}
          <Spacer $px={50} />
          <div className="controls">
            {canEditSettings && (
              <>
                <Button
                  primary
                  type="button"
                  disabled={!canSubmit}
                  $constWidth={220}
                  onClick={() => {
                    mpEvent(MPEvents.ButtonClick, {
                      button: 'Update',
                      tags: ['ORGANISATION'],
                    });
                    handleSubmit();
                  }}
                >
                  {isUploadingLogo ? 'UPLOADING...' : 'UPDATE'}
                </Button>
              </>
            )}
            {organisation.actionsOnThisOrganisation?.canArchive && !organisation.isArchived && (
              <Button
                secondary
                type="submit"
                disabled={isArchiving}
                $constWidth={220}
                onClick={() => setArchiveWarningModalOpen(true)}
              >
                ARCHIVE
              </Button>
            )}
          </div>
        </form>
      </Div>
    </>
  );
};
